import { Avatar, AvatarGroup, Divider, Grid } from "@mui/material";
import MxCPropertyContentDetail from "./property-elements-card/MxCPropertyContentDetailLarge";
import { ICardProperty } from "../../../../model/card-property.model";
import MxCPropertyFeature from "./property-elements-card/MxCPropertyImprovements";
import MxTypography from "@rf-l1/MxTypography";
import { EFontFamilies } from "@/shared/theme/theme";
import MxIcons from "@rf-l1/MxIcons";
import { IconDotsVertical, IconMapPin } from "@tabler/icons-react";
import MxTooltipBasic from "@rf-l1/tooltip/MxTooltipBasic";
import MxCPropertyStatus from "./property-elements-card/MxCPropertyStatus";
import MxCPropertyStans from "./property-elements-card/MxCPropertyStans";
import MxCPropertyPictureLarge from "./property-elements-card/MxCPropertyPictureLarge";
import MxMenu, { MxMenuItemProps } from "@rf-l1/MxMenu";
import { useMemo } from "react";
import { getConditionStatusLabel } from "@/src-refactor/modules/shared/components/refactor/helpers/property/prepare-condition-status-label";

export interface MxCardPropertyLarge extends ICardProperty {
  onClick?: (e: ICardProperty) => void;
  itemsMenu?: MxMenuItemProps[];
}

const MxCardPropertyLarge = (props: MxCardPropertyLarge) => {
  const conditionLabel = useMemo(() => {
    return getConditionStatusLabel(props.conditionStatus, props.businessType);
  }, [props.conditionStatus, props.businessType]);

  const cardProps = {
    ...props,
    conditionStatus: conditionLabel
  };

  return (
    <Grid
      id={cardProps.id + "-property"}
      container
      bgcolor={"background.paper"}
      sx={{
        border: "2px solid #ccc",
        borderRadius: 0.5,
        overflow: "hidden"
      }}
    >
      <Grid item xs={12} md={5}>
        <MxCPropertyPictureLarge pictures={cardProps.pictures} />
      </Grid>

      <MxCPropertyContentDetail
        xs={12}
        md={7}
        alignContent="space-between"
        rowGap={1}
      >
        <Grid container item xs={12} rowGap={1}>
          <Grid container item xs={12} columnSpacing={0.5} alignItems="flex-start">
            <Grid item xs={cardProps.itemsMenu?.length ? 11 : 12}>
              <MxTypography
                variant="caption"
                color="black"
                fontWeight={800}
                lines={2}
                fontFamily={EFontFamilies.DEFAULT_FONT}
              >
                {cardProps.title}
              </MxTypography>
            </Grid>

            {Boolean(cardProps.itemsMenu?.length) && (
              <Grid
                item
                xs={1}
                container
                justifyContent="flex-end"
                onClick={event => {
                  event.stopPropagation();
                }}
              >
                <MxMenu items={cardProps.itemsMenu ?? []} width={"auto"}>
                  <MxIcons size={16} color="primary.main">
                    <IconDotsVertical size={16} stroke={1.5} />
                  </MxIcons>
                </MxMenu>
              </Grid>
            )}
          </Grid>

          {Boolean(cardProps?.nameProperty) && (
            <Grid item xs={12}>
              <MxTypography
                variant="overline"
                color="grey.700"
                fontWeight={400}
                lines={1}
                children={cardProps?.nameProperty}
              />
            </Grid>
          )}
        </Grid>
        <Grid container item xs={12} rowGap={1} alignItems="flex-start">
          <Grid container item xs={12} spacing={0.3} alignItems={"center"}>
            {cardProps.prices.map((price, i) => (
              <Grid
                container
                item
                xs={12}
                spacing={0.5}
                alignItems={"center"}
                key={i}
              >
                <Grid item xs="auto">
                  <MxTypography
                    variant="caption"
                    color={i === 0 ? "grey.700" : "primary.main"}
                    fontWeight={i === 0 ? 400 : 600}
                    fontFamily={EFontFamilies.SECONDARY_FONT}
                    textTransform="uppercase"
                    sx={{
                      fontSize: "11.5px !important"
                    }}
                  >
                    {price.label}
                  </MxTypography>
                </Grid>
                <Grid item xs="auto">
                  <MxTypography
                    variant="caption"
                    color="primary.main"
                    fontWeight={600}
                    fontSize={12}
                    fontFamily={EFontFamilies.DEFAULT_FONT}
                  >
                    {price.value}
                  </MxTypography>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Grid
            item
            xs={12}
            container
            columnSpacing={0.5}
            flexWrap="nowrap"
            alignItems="center"
          >
            <Grid item xs="auto" width={10}>
              <MxIcons
                color="grey.700"
                children={<IconMapPin size={16} stroke={1.5} />}
              />
            </Grid>
            <Grid item xs={11}>
              <MxTypography
                fontWeight={600}
                variant="overline"
                color="grey.700"
                lines={1}
              >
                {cardProps.location}
              </MxTypography>
            </Grid>
          </Grid>

          <Grid container item xs={12} alignItems="center" columnSpacing={0.5}>
            <Grid item xs="auto">
              <MxTypography fontWeight={400} variant="overline" color="grey.700">
                {cardProps?.agentsInfoFull?.length > 1
                  ? "Agentes Captadores"
                  : "Agente Captador"}
              </MxTypography>
            </Grid>

            <Grid item xs="auto">
              <AvatarGroup
                max={4}
                sx={{
                  "& .MuiAvatar-root": {
                    width: 25,
                    height: 25
                  },
                  "*": {
                    fontSize: "10px"
                  }
                }}
              >
                {cardProps?.agentsInfoFull?.map((user, index) => (
                  <MxTooltipBasic msg={user.name} key={index}>
                    <Avatar
                      sx={{
                        width: 25,
                        height: 25,
                        // backgroundColor: randomColor(),
                        fontSize: "10px"
                      }}
                      alt={user.name}
                      src={user.picture ?? undefined}
                      key={index}
                    >
                      {user.name.charAt(0).toUpperCase()}
                      {user.name.charAt(1).toUpperCase()}
                    </Avatar>
                  </MxTooltipBasic>
                ))}
              </AvatarGroup>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
            {cardProps.feature && <MxCPropertyFeature {...cardProps.feature} />}
            <Divider sx={{ my: 0.5 }} />
          </Grid>

          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            item
            xs={12}
          >
            {Boolean(cardProps.status) && (
              <Grid item xs="auto">
                <MxCPropertyStatus title={cardProps.status ?? ""} />
              </Grid>
            )}

            <Grid item xs="auto">
              <MxTypography
                variant="overline"
                color="grey.700"
                children={`${cardProps.commissionGaranted}%`}
              />
            </Grid>

            <Grid item xs="auto">
              <MxCPropertyStans views={cardProps.views} likes={cardProps.likes} />
            </Grid>
          </Grid>
        </Grid>
      </MxCPropertyContentDetail>
    </Grid>
  );
};

export default MxCardPropertyLarge;
